/** Thin API client. Every response uses the { ok, data, meta, error } envelope. */

export interface ApiFailure {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public details?: unknown) {
    super(message);
  }
}

const TOKEN_KEY = 'femi9.token';
const SESSION_KEY = 'femi9.session';

export interface Session {
  token: string;
  user: {
    id: string; name: string; nameAr?: string | null; empCode: string;
    role: string; storeCodes: string[]; scopes: string[]; discountLimitPct: number;
  };
  storeCode: string;
  terminalCode: string;
}

export const session = {
  get(): Session | null {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  },
  set(s: Session) {
    localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    localStorage.setItem(TOKEN_KEY, s.token);
  },
  clear() {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(TOKEN_KEY);
  },
  token(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
  has(scope: string): boolean {
    return this.get()?.user.scopes.includes(scope) ?? false;
  },
};

/**
 * API base. Empty in development (Vite proxies /api). In production the POS may
 * be mounted under a path prefix behind an existing reverse proxy — e.g.
 * https://host/pos/ with the API at /pos/api — so the prefix is injected at
 * build time rather than hardcoded.
 */
export const API_BASE: string =
  ((import.meta as any).env?.VITE_API_BASE as string | undefined)?.replace(/\/$/, '') ?? '';

export async function api<T = unknown>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const token = session.token();
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
  const res = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  let json: { ok: boolean; data: T; error: ApiFailure | null };
  try {
    json = await res.json();
  } catch {
    throw new ApiError(res.status, 'BAD_RESPONSE', `Server returned ${res.status}`);
  }

  if (!res.ok || !json.ok) {
    const err = json.error ?? { code: 'ERROR', message: `HTTP ${res.status}` };
    if (res.status === 401) session.clear();
    throw new ApiError(res.status, err.code, err.message, err.details);
  }
  return json.data;
}

export const get = <T>(p: string) => api<T>('GET', p);
export const post = <T>(p: string, b?: unknown) => api<T>('POST', p, b);
export const patch = <T>(p: string, b?: unknown) => api<T>('PATCH', p, b);
export const put = <T>(p: string, b?: unknown) => api<T>('PUT', p, b);

/** BHD formatting. Money is always integer fils. */
export function fmt(fils: number | null | undefined): string {
  if (fils === null || fils === undefined || Number.isNaN(fils)) return '0.000';
  const neg = fils < 0;
  const abs = Math.abs(Math.round(fils));
  return `${neg ? '-' : ''}${Math.floor(abs / 1000)}.${String(abs % 1000).padStart(3, '0')}`;
}

export const fmtBhd = (fils: number | null | undefined) => `BHD ${fmt(fils)}`;

/** Parse user-typed BHD into fils. */
export function parseBhd(text: string): number {
  const t = text.trim();
  if (!t) return 0;
  if (!/^-?\d*(\.\d*)?$/.test(t)) throw new Error(`"${text}" is not a valid amount`);
  const neg = t.startsWith('-');
  const [w, f = ''] = t.replace('-', '').split('.');
  const fils = Number(w || 0) * 1000 + Number((f + '000').slice(0, 3) || 0);
  return neg ? -fils : fils;
}
